import gql from 'graphql-tag';
import {
  CellCenterV8,
  CellDateV8,
  FlexRow,
  TablePaginatedV8,
  usePaginatedQueryV8,
} from 'motion-components';
import React, { useMemo, useState } from 'react';
import { Button, Card, Col, Modal, ModalProps, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { createModal } from 'react-modal-promise';
import { ButtonCloseModal } from 'shared-pages/components';
import { useRootStore } from 'store';
import LoadingOverlay from '@achmadk/react-loading-overlay';

export const gqlGetQuery = gql`
  query getCntContributionSend2Erp($limit: Int, $offset: Int, $order: String, $where: String) {
    getCntContributionSend2Erp(limit: $limit, offset: $offset, order: $order, where: $where) {
      data {
        id
        contributionId
        submittedByUser
        submittedDate
        action
        message
        jsonsend
        erpresult
      }
      recordCount
    }
  }
`;
type Props = {
  readonly open: boolean;
  readonly close: (result: any) => void;
  readonly title: string;
  /** body of dialog */
  readonly collectorId?: number;
} & ModalProps;

const t = (value) => value;

function parseJsonField(value: unknown): { entries: [string, unknown][]; raw: string } {
  if (value == null || value === '') {
    return { entries: [], raw: '' };
  }
  if (typeof value === 'object') {
    return { entries: Object.entries(value as Record<string, unknown>), raw: JSON.stringify(value) };
  }
  const str = String(value);
  try {
    const parsed = JSON.parse(str);
    if (parsed && typeof parsed === 'object') {
      return { entries: Object.entries(parsed), raw: str };
    }
    return { entries: [['value', parsed]], raw: str };
  } catch {
    return { entries: [], raw: str };
  }
}

const columns = [
  {
    id: 'submittedByUser',
    header: t('Χρήστης'),
    columnName: t('Χρήστης'),
    accessorKey: 'submittedByUser',
    orderByField: 'submittedByUser',
    cell: CellCenterV8,
    size: 5,
  },
  {
    id: 'submittedDate',
    header: t('Ημερομηνία'),
    columnName: t('submittedDate'),
    accessorKey: 'submittedDate',
    orderByField: 'submittedDate',
    cell: CellDateV8,
    size: 5,
  },
  {
    id: 'action',
    header: t('Αποστολή'),
    columnName: t('Αποστολή'),
    accessorKey: 'action',
    orderByField: 'action',
    cell: CellCenterV8,
    size: 5,
  },
  {
    id: 'message',
    header: t('Μήνυμα'),
    columnName: t('Μήνυμα'),
    accessorKey: 'message',
    orderByField: 'message',
    cell: CellCenterV8,
    size: 5,
  },
  {
    id: 'erpresult',
    header: t('Απόκριση ERP'),
    columnName: t('Απόκριση ERP'),
    accessorKey: 'erpresult',
    orderByField: 'erpresult',
    cell: ({ row }) => {
      const { entries, raw } = parseJsonField(row.original.erpresult);

      return (
        <div>
          {entries.length > 0
            ? entries.map(([key, val]) => (
                <Row key={key} className="p-1">
                  <Col xs={3}>{key}:</Col>
                  <Col xs={9}>{JSON.stringify(val)}</Col>
                </Row>
              ))
            : raw && <pre className="m-0 small text-wrap">{raw}</pre>}
        </div>
      );
    },
    size: 35,
  },
  {
    id: 'jsonsend',
    header: t('Απεσταλμένα Στοιχεία'),
    columnName: t('Απεσταλμένα Στοιχεία'),
    accessorKey: 'jsonsend',
    orderByField: 'jsonsend',
    cell: ({ row }) => {
      const { entries, raw } = parseJsonField(row.original.jsonsend);

      return (
        <div>
          {entries.length > 0
            ? entries.map(([key, val]) => (
                <Row key={key} className="p-1">
                  <Col xs={2}>{key}:</Col>
                  <Col xs={10}>{JSON.stringify(val)}</Col>
                </Row>
              ))
            : raw && <pre className="m-0 small text-wrap">{raw}</pre>}
        </div>
      );
    },
    size: 25,
  },
];

const ModalGrid = ({ open, close, contributionId }: Props) => {
  const { t } = useTranslation();
  const { appStore, tableStore } = useRootStore();
  const query = usePaginatedQueryV8(
    gqlGetQuery,
    (values) => {
      return JSON.stringify({ contributionId });
    },
    'submittedDate DESC',
    appStore,
    true,
    {},
    false, // History Replace
  );
  const {
    loading,
    data,
    dataCount,
    error,
    initPageIndex,
    filtersChanged,
    gridPageChanged,
    queryState,
    queryParams,
    setQueryParams,
    refetch,
  } = query;

  return (
    <>
      <Modal size="xl" show={open} centered backdrop="static">
        <Card>
          <Card.Header>
            <FlexRow>
              <Card.Title className="m-0">{t('Αποστολές Εισφοράς προς το Erp')}</Card.Title>
              <ButtonCloseModal
                onClick={() => {
                  close(-1);
                }}
              />
            </FlexRow>
          </Card.Header>
        </Card>
        <Card.Body className="nopadding">
          <LoadingOverlay active={loading} spinner text={'Παρακαλώ περιμένετε...'}>
            {data ? (
              <TablePaginatedV8
                loading={loading}
                id="cnt2erp-grid"
                appStore={appStore}
                columns={columns as any}
                data={data}
                dataCount={dataCount}
                onPageChange={gridPageChanged}
                initPageIndex={0}
                fixedPageSize={50}
                initSorting={[{ id: 'id', desc: true }]}
              ></TablePaginatedV8>
            ) : (
              <div style={{ height: '200px' }}></div>
            )}
          </LoadingOverlay>
        </Card.Body>
      </Modal>
    </>
  );
};

export const promiseShowCnt2ErpInfo = createModal(ModalGrid);
